import { clamp01 } from '@/core/math/scalar';
import { SIGMA_SB } from '@/core/units';
import { RadiatorDef, ShipDef } from './ShipDef';

export interface ThermalInput {
  /** Convective heating flux from the airflow, W/m^2. */
  aeroHeatFlux: number;
  /** Area exposed to the airflow, m^2. */
  exposedArea: number;
  /** Waste heat from engines and subsystems, watts. */
  internalHeat: number;
  /** Stellar irradiance at the vessel, W/m^2. */
  irradiance: number;
  /** Ambient air temperature, K. Used for convective cooling. */
  ambientTemperature: number;
  /** Ambient air density, kg/m^3. Drives convective cooling strength. */
  ambientDensity: number;
}

export interface ThermalReport {
  hullTemperature: number;
  /** Net watts into the hull this step. */
  netHeat: number;
  /** Watts rejected by the radiators. */
  radiated: number;
  /** Fraction of the damage threshold reached, 0..1+. */
  thermalLoad: number;
  overheating: boolean;
}

/**
 * Hull thermal model.
 *
 * Heat in: aerodynamic (stagnation) heating, engine and subsystem waste heat,
 * and stellar irradiance. Heat out: radiator emission following the
 * Stefan-Boltzmann law, plus convection when there is air to convect into.
 *
 * The asymmetry between those two is what makes re-entry a real constraint:
 * radiators reject heat proportional to T^4 but with a modest area, while
 * stagnation heating scales as sqrt(density) * v^3 and can exceed the
 * radiators by three orders of magnitude for a minute or two. Surviving entry
 * is about thermal *capacity* and time, not about steady-state balance.
 */
export class ThermalSystem {
  /** Hull temperature, K. */
  temperature = 290;
  private readonly heatCapacity: number;
  private readonly limit: number;
  private readonly radiators: RadiatorDef[];
  /** Whether deployable radiators are extended. */
  radiatorsDeployed = true;

  lastRadiated = 0;
  lastNetHeat = 0;

  constructor(def: ShipDef) {
    this.heatCapacity = def.hullHeatCapacity;
    this.limit = def.hullTemperatureLimit;
    this.radiators = def.radiators;
  }

  get temperatureLimit(): number {
    return this.limit;
  }

  update(input: ThermalInput, dt: number): ThermalReport {
    const T = this.temperature;

    const aeroIn = Math.max(0, input.aeroHeatFlux) * Math.max(0, input.exposedArea);
    const solarIn = Math.max(0, input.irradiance) * this.effectiveSolarArea();
    const heatIn = aeroIn + Math.max(0, input.internalHeat) + solarIn;

    // Radiative rejection. Space is treated as a 4 K background.
    let radiated = 0;
    for (const r of this.radiators) {
      if (r.deployable && !this.radiatorsDeployed) continue;
      radiated += r.emissivity * SIGMA_SB * r.area * (T ** 4 - 4 ** 4);
    }
    radiated = Math.max(0, radiated);

    // Convective exchange with the atmosphere. Positive when the hull is hotter
    // than the air, which is why a hot vessel cools rapidly in a dense
    // atmosphere once it has slowed down.
    const convection = 26 * Math.sqrt(Math.max(0, input.ambientDensity)) * 40 * (T - input.ambientTemperature);

    const net = heatIn - radiated - convection;
    this.temperature = Math.max(4, T + (net * dt) / Math.max(1, this.heatCapacity));

    this.lastRadiated = radiated;
    this.lastNetHeat = net;

    return {
      hullTemperature: this.temperature,
      netHeat: net,
      radiated,
      thermalLoad: this.temperature / this.limit,
      overheating: this.temperature > this.limit,
    };
  }

  /** Damage accumulated this step from over-temperature, 0..1. */
  overheatDamage(dt: number): number {
    if (this.temperature <= this.limit) return 0;
    const over = (this.temperature - this.limit) / this.limit;
    return clamp01(over * over * dt * 0.6);
  }

  private effectiveSolarArea(): number {
    let area = 0;
    for (const r of this.radiators) {
      if (r.deployable && !this.radiatorsDeployed) continue;
      // Radiators absorb as well as emit; a modest absorptivity keeps sunward
      // orientation relevant without dominating the budget.
      area += r.area * 0.12;
    }
    return area + 12;
  }
}
